import {
  outerBorderRadiusExtension,
  shapeRadius,
  starInnerRadius,
  starRadius,
} from './shapeUtils';

// Liens entre les sous-formes d'une forme composée. Chaque lien est rangé dans
// la sous-forme d'où il part (`sub.links`, `to` = id de la sous-forme d'arrivée)
// plutôt qu'à part dans `box.content` : la banque de formes n'enregistre que le
// tableau des sous-formes (`definition`, cf. Shape.jsx#handleSaveToLibrary et
// Proposals::Shape côté back) — les liens y voyagent ainsi sans changer son
// format. Seuls les points d'accroche ne sont jamais stockés : ils se recalculent
// depuis la géométrie des deux formes, quelle que soit la façon dont elles ont
// bougé (glisser, panneau, redimensionnement de la box ou d'un groupe).

export const LINK_TYPES = ['straight', 'elbow', 'curved'];
export const LINK_MARKERS = ['none', 'arrow', 'square', 'diamond', 'dot'];
// Ancres d'une forme : milieu de chaque côté et coins, dans le sens horaire.
// `fromAnchor`/`toAnchor` d'un lien en nomment une ; absentes, l'extrémité est
// placée automatiquement (côté qui fait face à l'autre forme).
export const LINK_ANCHORS = ['top', 'top-right', 'right', 'bottom-right', 'bottom', 'bottom-left', 'left', 'top-left'];

export const createLink = ({ id, to, fromAnchor, toAnchor }) => ({
  id,
  to,
  ...(fromAnchor ? { fromAnchor } : {}),
  ...(toAnchor ? { toAnchor } : {}),
  type: 'straight',
  color: '#000000',
  strokeWidth: 2,
  dash: false,
  startMarker: 'none',
  endMarker: 'arrow',
});

// ---------------------------------------------------------------------------
// Données

// Liens affichables : cible existante et différente de la source. Un lien vers
// une sous-forme disparue n'est jamais dessiné, même s'il traîne encore.
export const collectLinks = (shapes = []) => {
  const byId = new Map(shapes.map((sub) => [sub.id, sub]));
  return shapes.flatMap((from) => (from.links ?? [])
    .filter((link) => link.to !== from.id && byId.has(link.to))
    .map((link) => ({ ownerId: from.id, link, from, to: byId.get(link.to) })));
};

export const findLink = (shapes = [], ownerId, linkId) =>
  shapes.find((sub) => sub.id === ownerId)?.links?.find((link) => link.id === linkId) ?? null;

export const hasLinkBetween = (shapes, aId, bId) => collectLinks(shapes).some(
  ({ from, to }) => (from.id === aId && to.id === bId) || (from.id === bId && to.id === aId)
);

export const mutateAddLink = (box, fromId, toId, linkId, { fromAnchor, toAnchor } = {}) => {
  const shapes = box.content?.shapes ?? [];
  const from = shapes.find((sub) => sub.id === fromId);
  if (!from || fromId === toId || !shapes.some((sub) => sub.id === toId)) return false;
  if (hasLinkBetween(shapes, fromId, toId)) return false;
  from.links = [...(from.links ?? []), createLink({ id: linkId, to: toId, fromAnchor, toAnchor })];
  return true;
};

export const mutateUpdateLink = (box, ownerId, linkId, mutator) => {
  const link = findLink(box.content?.shapes, ownerId, linkId);
  if (link) mutator(link);
};

// Un tableau vide est retiré : une sous-forme sans lien doit rester identique à
// celle qu'elle était avant (cf. mutateCollapseUnchangedSubShape).
const pruneLinks = (sub, keep) => {
  if (!sub.links) return;
  sub.links = sub.links.filter(keep);
  if (sub.links.length === 0) delete sub.links;
};

// Raccroche une extrémité (`end` : 'from' ou 'to') d'un lien à l'ancre
// `anchor` de la sous-forme `subId` — la même ou une autre. Le lien part
// toujours de la sous-forme qui le porte : changer son départ de forme le
// déménage donc dans la nouvelle. Renvoie l'id de la sous-forme qui le porte
// ensuite, ou null si le geste est refusé (lien d'une forme vers elle-même, ou
// doublon d'un lien existant entre les deux mêmes formes).
export const mutateRetargetLink = (box, ownerId, linkId, end, subId, anchor) => {
  const shapes = box.content?.shapes ?? [];
  const owner = shapes.find((sub) => sub.id === ownerId);
  const link = owner?.links?.find((l) => l.id === linkId);
  if (!link || !shapes.some((sub) => sub.id === subId)) return null;
  if (end === 'to') {
    if (subId === ownerId) return null;
    if (subId !== link.to && hasLinkBetween(shapes, ownerId, subId)) return null;
    link.to = subId;
    link.toAnchor = anchor;
    return ownerId;
  }
  if (subId === link.to) return null;
  if (subId === ownerId) {
    link.fromAnchor = anchor;
    return ownerId;
  }
  if (hasLinkBetween(shapes, subId, link.to)) return null;
  const moved = { ...link, fromAnchor: anchor };
  pruneLinks(owner, (l) => l.id !== linkId);
  const newOwner = shapes.find((sub) => sub.id === subId);
  newOwner.links = [...(newOwner.links ?? []), moved];
  return subId;
};

export const mutateRemoveLink = (box, ownerId, linkId) => {
  const owner = box.content?.shapes?.find((sub) => sub.id === ownerId);
  if (owner) pruneLinks(owner, (link) => link.id !== linkId);
};

export const mutateRemoveLinksTo = (box, subShapeId) => {
  (box.content?.shapes ?? []).forEach((sub) => pruneLinks(sub, (link) => link.to !== subShapeId));
};

// Nouveaux ids pour toutes les sous-formes (insertion depuis la banque, où la
// même forme peut arriver plusieurs fois dans un document) : les liens suivent
// leurs extrémités, et ceux dont la cible manque sont abandonnés.
export const remapSubShapeIds = (shapes = [], makeId) => {
  const ids = new Map(shapes.map((sub) => [sub.id, makeId()]));
  return shapes.map((sub) => {
    const next = { ...sub, id: ids.get(sub.id) };
    const links = (sub.links ?? [])
      .filter((link) => ids.has(link.to) && link.to !== sub.id)
      .map((link) => ({ ...link, id: makeId(), to: ids.get(link.to) }));
    if (links.length) next.links = links;
    else delete next.links;
    return next;
  });
};

// ---------------------------------------------------------------------------
// Géométrie

const ELLIPSE_SEGMENTS = 48;
const CORNER_SEGMENTS = 8;

const ellipsePoints = (cx, cy, rx, ry) => Array.from({ length: ELLIPSE_SEGMENTS }, (_, i) => {
  const a = (i / ELLIPSE_SEGMENTS) * Math.PI * 2;
  return [cx + rx * Math.cos(a), cy + ry * Math.sin(a)];
});

// Sommets d'un Konva.RegularPolygon/Star : le premier en haut, sens horaire.
const radialPoints = (cx, cy, radii) => radii.map((r, i) => {
  const a = (i / radii.length) * Math.PI * 2;
  return [cx + r * Math.sin(a), cy - r * Math.cos(a)];
});

// Rectangle aux coins arrondis (ordre Konva : haut-gauche, haut-droite,
// bas-droite, bas-gauche), arcs échantillonnés — sans eux, l'accroche tombe dans
// le vide d'un coin dès que le lien arrive en diagonale.
const roundedRectPoints = (w, h, radii) => {
  const max = Math.min(w, h) / 2;
  const [tl, tr, br, bl] = radii.map((r) => Math.max(0, Math.min(r ?? 0, max)));
  const corners = [
    [w - tr, tr, tr, -Math.PI / 2],
    [w - br, h - br, br, 0],
    [bl, h - bl, bl, Math.PI / 2],
    [tl, tl, tl, Math.PI],
  ];
  return corners.flatMap(([cx, cy, r, start]) => (r === 0
    ? [[cx, cy]]
    : Array.from({ length: CORNER_SEGMENTS + 1 }, (_, i) => {
      const a = start + (i / CORNER_SEGMENTS) * (Math.PI / 2);
      return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
    })));
};

const cornerRadiiOf = (sub) => {
  if (Array.isArray(sub.cornerRadii)) return sub.cornerRadii;
  return Array(4).fill(sub.radius ?? 0);
};

// Contour d'une sous-forme dans son repère local non tourné ([0, width] ×
// [0, height]), avec le point d'où partent les rayons d'accroche — mêmes
// formules que getShapeElement (shapeUtils.js), pour que le lien touche le bord
// réellement dessiné. Un SVG ou une flèche s'accroche à son cadre.
export const getLocalOutline = (sub) => {
  const w = sub.width;
  const h = sub.height;
  const border = sub.borders?.size ?? 0;
  switch (sub.shape) {
    case 'square':
      return { center: [w / 2, w / 2], points: roundedRectPoints(w, w, cornerRadiiOf(sub)) };
    case 'rectangle':
      return { center: [w / 2, h / 2], points: roundedRectPoints(w, h, cornerRadiiOf(sub)) };
    case 'round':
    case 'ring':
      return { center: [w / 2, w / 2], points: ellipsePoints(w / 2, w / 2, w / 2, w / 2) };
    case 'ellipse':
      return { center: [w / 2, h / 2], points: ellipsePoints(w / 2, h / 2, w / 2, h / 2) };
    case 'triangle': {
      const r = shapeRadius('triangle', w);
      return { center: [w / 2, r], points: radialPoints(w / 2, r, [r, r, r]) };
    }
    case 'hexagone': {
      const r = (h - border) / 2;
      return { center: [w / 2, h / 2], points: radialPoints(w / 2, h / 2, Array(6).fill(r)) };
    }
    case 'star': {
      const outer = starRadius(w, border);
      const inner = starInnerRadius(w, border);
      const cy = outer + outerBorderRadiusExtension(border);
      return {
        center: [w / 2, cy],
        points: radialPoints(w / 2, cy, Array.from({ length: 10 }, (_, i) => (i % 2 === 0 ? outer : inner))),
      };
    }
    default:
      return { center: [w / 2, h / 2], points: [[0, 0], [w, 0], [w, h], [0, h]] };
  }
};

const normalize = ([x, y]) => {
  const length = Math.hypot(x, y);
  return length < 1e-9 ? null : [x / length, y / length];
};

const rotate = ([x, y], angle) => {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return [x * cos - y * sin, x * sin + y * cos];
};

// Repère local → repère de composition : le Group de la sous-forme pivote
// autour du centre de son cadre (cf. getShapeComponent).
const toComposition = (sub, [lx, ly]) => {
  const [rx, ry] = rotate([lx - sub.width / 2, ly - sub.height / 2], ((sub.rotation ?? 0) * Math.PI) / 180);
  return [(sub.x ?? 0) + sub.width / 2 + rx, (sub.y ?? 0) + sub.height / 2 + ry];
};

// Plus grande distance t ≥ 0 à laquelle le rayon origin + t·dir coupe le
// contour : le bord le plus extérieur, y compris pour une étoile (non convexe).
const rayExit = ([ox, oy], [dx, dy], points) => {
  let best = 0;
  points.forEach(([ax, ay], i) => {
    const [bx, by] = points[(i + 1) % points.length];
    const ex = bx - ax;
    const ey = by - ay;
    const denom = dx * ey - dy * ex;
    if (Math.abs(denom) < 1e-12) return;
    const t = ((ax - ox) * ey - (ay - oy) * ex) / denom;
    const u = ((ax - ox) * dy - (ay - oy) * dx) / denom;
    if (t >= 0 && u >= -1e-9 && u <= 1 + 1e-9 && t > best) best = t;
  });
  return best;
};

export const getAnchorOrigin = (sub) => toComposition(sub, getLocalOutline(sub).center);

const radiansOf = (sub) => ((sub.rotation ?? 0) * Math.PI) / 180;

const outlineBounds = (points) => ({
  minX: Math.min(...points.map((p) => p[0])),
  maxX: Math.max(...points.map((p) => p[0])),
  minY: Math.min(...points.map((p) => p[1])),
  maxY: Math.max(...points.map((p) => p[1])),
});

// Direction (repère local) d'une ancre depuis le centre du contour : un côté
// tout droit, un coin vers le coin du cadre du contour — le coin lui-même pour
// un rectangle, le sommet pour la base d'un triangle, le point du bord dans
// cette direction pour une forme ronde.
const localAnchorDirection = (anchor, outline) => {
  const [cx, cy] = outline.center;
  const { minX, maxX, minY, maxY } = outlineBounds(outline.points);
  switch (anchor) {
    case 'top': return [0, -1];
    case 'right': return [1, 0];
    case 'bottom': return [0, 1];
    case 'left': return [-1, 0];
    case 'top-left': return normalize([minX - cx, minY - cy]);
    case 'top-right': return normalize([maxX - cx, minY - cy]);
    case 'bottom-right': return normalize([maxX - cx, maxY - cy]);
    case 'bottom-left': return normalize([minX - cx, maxY - cy]);
    default: return null;
  }
};

// Ancre `anchor` de la sous-forme (repère de composition) : `point` sur le bord
// et `dir`, direction sortante (tournée avec la forme), ou null si l'ancre est
// inconnue.
export const getAnchor = (sub, anchor, outline = getLocalOutline(sub)) => {
  const localDir = localAnchorDirection(anchor, outline);
  if (!localDir) return null;
  const t = rayExit(outline.center, localDir, outline.points);
  const [cx, cy] = outline.center;
  return {
    anchor,
    point: toComposition(sub, [cx + localDir[0] * t, cy + localDir[1] * t]),
    dir: rotate(localDir, radiansOf(sub)),
  };
};

export const getAnchors = (sub) => {
  const outline = getLocalOutline(sub);
  return LINK_ANCHORS.map((anchor) => getAnchor(sub, anchor, outline));
};

const distance = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);

export const nearestAnchor = (sub, point) => getAnchors(sub)
  .reduce((best, candidate) => (distance(candidate.point, point) < distance(best.point, point) ? candidate : best));

const pointInPolygon = ([x, y], points) => {
  let inside = false;
  points.forEach(([ax, ay], i) => {
    const [bx, by] = points[(i + 1) % points.length];
    if ((ay > y) !== (by > y) && x < ((bx - ax) * (y - ay)) / (by - ay) + ax) inside = !inside;
  });
  return inside;
};

const toLocal = (sub, [px, py]) => {
  const [rx, ry] = rotate(
    [px - ((sub.x ?? 0) + sub.width / 2), py - ((sub.y ?? 0) + sub.height / 2)],
    -radiansOf(sub)
  );
  return [rx + sub.width / 2, ry + sub.height / 2];
};

// Sous-forme visée par le pointeur pendant le tracé d'un lien : la plus haute
// dont le contour le contient, sinon celle qui a une ancre à moins de
// `tolerance` — une ancre est sur le bord, on l'attrape aussi bien du dehors.
// Calcul géométrique plutôt que hit-test Konva : le trait des liens, posé pile
// sur les bords, intercepterait le pointeur.
export const subShapeAtPoint = (shapes, point, { tolerance = 0, excludeId } = {}) => {
  const candidates = shapes.filter((sub) => sub.id !== excludeId);
  const inside = [...candidates].reverse()
    .find((sub) => pointInPolygon(toLocal(sub, point), getLocalOutline(sub).points));
  if (inside) return inside;
  let best = null;
  let bestDistance = tolerance;
  candidates.forEach((sub) => getAnchors(sub).forEach(({ point: anchorPoint }) => {
    const d = distance(anchorPoint, point);
    if (d <= bestDistance) {
      best = sub;
      bestDistance = d;
    }
  }));
  return best;
};

// Point du bord de `sub` atteint en partant de son centre dans la direction
// `dir` (repère de composition).
export const getBorderPoint = (sub, dir, outline = getLocalOutline(sub)) => {
  const localDir = rotate(dir, (-(sub.rotation ?? 0) * Math.PI) / 180);
  const t = rayExit(outline.center, localDir, outline.points);
  const [cx, cy] = outline.center;
  return toComposition(sub, [cx + localDir[0] * t, cy + localDir[1] * t]);
};


const angleOf = ([fromX, fromY], [toX, toY]) => Math.atan2(toY - fromY, toX - fromX);

const snapToAxis = ([x, y]) => (Math.abs(x) >= Math.abs(y) ? [Math.sign(x) || 1, 0] : [0, Math.sign(y) || 1]);

const dedupe = (points) => points.filter((p, i) => i === 0
  || Math.abs(p[0] - points[i - 1][0]) > 1e-6 || Math.abs(p[1] - points[i - 1][1]) > 1e-6);

const straightGeometry = (from, to, originA, originB) => {
  const dir = normalize([originB[0] - originA[0], originB[1] - originA[1]]);
  if (!dir) return null;
  const start = getBorderPoint(from, dir);
  const end = getBorderPoint(to, [-dir[0], -dir[1]]);
  return { bezier: false, points: [start, end] };
};

// Côtés de sortie/entrée d'un lien coudé ou courbe : l'axe dominant entre les
// deux centres, sauf si les bords se chevauchent sur cet axe (le lien
// repartirait en arrière) — on essaie alors l'autre.
const cardinalAnchors = (from, to, originA, originB) => {
  const dx = originB[0] - originA[0];
  const dy = originB[1] - originA[1];
  const axes = Math.abs(dx) >= Math.abs(dy) ? ['x', 'y'] : ['y', 'x'];
  return axes.map((axis) => {
    const sign = (axis === 'x' ? dx : dy) >= 0 ? 1 : -1;
    const dirA = axis === 'x' ? [sign, 0] : [0, sign];
    const dirB = [-dirA[0], -dirA[1]];
    const start = getBorderPoint(from, dirA);
    const end = getBorderPoint(to, dirB);
    const gap = axis === 'x' ? (end[0] - start[0]) * sign : (end[1] - start[1]) * sign;
    return { axis, dirA, dirB, start, end, gap };
  }).find((candidate) => candidate.gap > 0) ?? null;
};

const elbowGeometry = (anchors) => {
  const { axis, start, end } = anchors;
  const points = axis === 'x'
    ? [start, [(start[0] + end[0]) / 2, start[1]], [(start[0] + end[0]) / 2, end[1]], end]
    : [start, [start[0], (start[1] + end[1]) / 2], [end[0], (start[1] + end[1]) / 2], end];
  return { bezier: false, points: dedupe(points) };
};

// Sans les points alignés : un coude « plat » n'en est pas un.
const simplify = (points) => dedupe(points).filter((p, i, all) => {
  if (i === 0 || i === all.length - 1) return true;
  const [ax, ay] = all[i - 1];
  const [bx, by] = all[i + 1];
  return Math.abs((p[0] - ax) * (by - ay) - (p[1] - ay) * (bx - ax)) > 1e-6;
});

// Longueur du segment qui sort tout droit d'une ancre avant le premier coude.
const ELBOW_STUB = 16;

// Cadre (axes alignés, repère de composition) d'une sous-forme, rotation
// comprise : l'obstacle que le tracé d'un coude doit contourner.
const compositionBounds = (sub) => outlineBounds(getLocalOutline(sub).points.map((p) => toComposition(sub, p)));

// Un segment horizontal ou vertical passe-t-il à l'intérieur du cadre (le
// longer ou en partir depuis le bord ne compte pas) ?
const crossesBounds = ([ax, ay], [bx, by], { minX, maxX, minY, maxY }) => {
  const e = 1e-6;
  if (Math.abs(ay - by) < e) {
    return ay > minY + e && ay < maxY - e && Math.max(ax, bx) > minX + e && Math.min(ax, bx) < maxX - e;
  }
  return ax > minX + e && ax < maxX - e && Math.max(ay, by) > minY + e && Math.min(ay, by) < maxY - e;
};

// Tracé orthogonal entre deux ancres fixées, sorties `dirA`/`dirB` (déjà sur un
// axe). Candidats : deux en L, deux en Z, et quatre détours par un couloir qui
// longe les deux formes par l'extérieur (au-dessus, en dessous, à gauche, à
// droite). On garde celui qui ne repart pas en arrière en sortant de A, n'entre
// pas dans B par le côté opposé à son ancre, ne traverse aucune des deux formes
// (`obstacles`), puis qui a le moins de coudes et, à égalité, le plus court.
const orthogonalRoute = (a, dirA, b, dirB, obstacles = []) => {
  const a1 = [a[0] + dirA[0] * ELBOW_STUB, a[1] + dirA[1] * ELBOW_STUB];
  const b1 = [b[0] + dirB[0] * ELBOW_STUB, b[1] + dirB[1] * ELBOW_STUB];
  const mx = (a1[0] + b1[0]) / 2;
  const my = (a1[1] + b1[1]) / 2;
  const candidates = [
    [a1, [b1[0], a1[1]], b1],
    [a1, [a1[0], b1[1]], b1],
    [a1, [mx, a1[1]], [mx, b1[1]], b1],
    [a1, [a1[0], my], [b1[0], my], b1],
  ];
  if (obstacles.length) {
    const top = Math.min(a1[1], b1[1], ...obstacles.map((o) => o.minY - ELBOW_STUB));
    const bottom = Math.max(a1[1], b1[1], ...obstacles.map((o) => o.maxY + ELBOW_STUB));
    const left = Math.min(a1[0], b1[0], ...obstacles.map((o) => o.minX - ELBOW_STUB));
    const right = Math.max(a1[0], b1[0], ...obstacles.map((o) => o.maxX + ELBOW_STUB));
    candidates.push(
      [a1, [a1[0], top], [b1[0], top], b1],
      [a1, [a1[0], bottom], [b1[0], bottom], b1],
      [a1, [left, a1[1]], [left, b1[1]], b1],
      [a1, [right, a1[1]], [right, b1[1]], b1],
    );
  }
  const dot = (u, v) => u[0] * v[0] + u[1] * v[1];
  const score = (middle) => {
    const route = dedupe([a1, ...middle.slice(1, -1), b1]);
    let penalty = 0;
    if (route.length > 1) {
      if (dot([route[1][0] - a1[0], route[1][1] - a1[1]], dirA) < -1e-6) penalty += 100;
      const n = route.length;
      if (dot([b1[0] - route[n - 2][0], b1[1] - route[n - 2][1]], dirB) > 1e-6) penalty += 100;
    }
    const full = simplify([a, ...middle, b]);
    let length = 0;
    full.slice(1).forEach((p, i) => {
      length += distance(full[i], p);
      penalty += 50 * obstacles.filter((o) => crossesBounds(full[i], p, o)).length;
    });
    return penalty + full.length + length * 1e-4;
  };
  const best = candidates.reduce((winner, candidate) => (score(candidate) < score(winner) ? candidate : winner));
  return simplify([a, ...best, b]);
};

const fixedGeometry = (type, start, end, obstacles) => {
  if (distance(start.point, end.point) < 1e-6) return null;
  if (type === 'elbow') {
    return {
      bezier: false,
      points: orthogonalRoute(start.point, snapToAxis(start.dir), end.point, snapToAxis(end.dir), obstacles),
    };
  }
  if (type === 'curved') {
    return curvedGeometry({ dirA: start.dir, dirB: end.dir, start: start.point, end: end.point });
  }
  return { bezier: false, points: [start.point, end.point] };
};

// Extrémité « auto » d'un lien dont l'autre bout est fixé : le bord qui fait
// face à ce point (côté d'axe pour un coude ou une courbe).
const autoEnd = (sub, type, target) => {
  const origin = getAnchorOrigin(sub);
  const delta = normalize([target[0] - origin[0], target[1] - origin[1]]);
  if (!delta) return null;
  const dir = type === 'straight' ? delta : snapToAxis(delta);
  return { point: getBorderPoint(sub, dir), dir };
};

// Portée des points de contrôle d'une courbe, bornée : au-delà, deux ancres
// tournées du même côté la faisaient sortir loin de la zone d'affichage, qui la
// rognait.
const CURVE_MIN_REACH = 20;
const CURVE_MAX_REACH = 80;

const curvedGeometry = ({ dirA, dirB, start, end }) => {
  const k = Math.min(CURVE_MAX_REACH, Math.max(CURVE_MIN_REACH, Math.hypot(end[0] - start[0], end[1] - start[1]) / 2));
  return {
    bezier: true,
    points: [start, [start[0] + dirA[0] * k, start[1] + dirA[1] * k], [end[0] + dirB[0] * k, end[1] + dirB[1] * k], end],
  };
};

// Tracé d'un lien entre deux sous-formes (repère de composition), ou null si
// elles partagent le même centre. `points` : polyligne, ou [début, contrôle 1,
// contrôle 2, fin] d'une courbe de Bézier. `startAngle`/`endAngle` : direction
// vers laquelle pointe chaque extrémité, pour orienter les marqueurs.
export const computeLinkGeometry = (type, from, to, { fromAnchor, toAnchor } = {}) => {
  const fixedStart = fromAnchor ? getAnchor(from, fromAnchor) : null;
  const fixedEnd = toAnchor ? getAnchor(to, toAnchor) : null;
  let geometry = null;
  if (fixedStart || fixedEnd) {
    const start = fixedStart ?? autoEnd(from, type, fixedEnd.point);
    const end = fixedEnd ?? autoEnd(to, type, fixedStart.point);
    geometry = start && end ? fixedGeometry(type, start, end, [compositionBounds(from), compositionBounds(to)]) : null;
  } else {
    const originA = getAnchorOrigin(from);
    const originB = getAnchorOrigin(to);
    if (type === 'elbow' || type === 'curved') {
      const anchors = cardinalAnchors(from, to, originA, originB);
      if (anchors) geometry = type === 'elbow' ? elbowGeometry(anchors) : curvedGeometry(anchors);
    }
    geometry = geometry ?? straightGeometry(from, to, originA, originB);
  }
  if (!geometry) return null;
  const { points } = geometry;
  const n = points.length;
  return {
    ...geometry,
    startAngle: angleOf(points[1], points[0]),
    endAngle: angleOf(points[n - 2], points[n - 1]),
  };
};

// ---------------------------------------------------------------------------
// Dessin (appelé depuis le sceneFunc/hitFunc d'un Konva.Shape)

export const markerSize = (strokeWidth) => Math.max(8, strokeWidth * 4);

// Taille de chaque extrémité : celle choisie (`startMarkerSize`/
// `endMarkerSize`), sinon proportionnelle à l'épaisseur du trait.
export const markerSizes = ({ strokeWidth = 1, startMarkerSize, endMarkerSize } = {}) => ({
  start: startMarkerSize ?? markerSize(strokeWidth),
  end: endMarkerSize ?? markerSize(strokeWidth),
});

// Recul du trait sous un marqueur pointu, pour que l'extrémité du trait ne
// dépasse pas de la pointe (un trait épais déborderait d'une flèche fine).
const markerInset = (marker, size) => {
  if (marker === 'arrow') return size * 0.9;
  if (marker === 'diamond') return size;
  return 0;
};

const insetPoint = ([x, y], angle, distance) => [x - Math.cos(angle) * distance, y - Math.sin(angle) * distance];

export const traceLinkPath = (ctx, geometry, style = {}) => {
  const { startMarker = 'none', endMarker = 'none' } = style;
  const sizes = markerSizes(style);
  const points = geometry.points.slice();
  const n = points.length;
  points[0] = insetPoint(points[0], geometry.startAngle, markerInset(startMarker, sizes.start));
  points[n - 1] = insetPoint(points[n - 1], geometry.endAngle, markerInset(endMarker, sizes.end));
  ctx.beginPath();
  ctx.moveTo(points[0][0], points[0][1]);
  if (geometry.bezier) {
    ctx.bezierCurveTo(points[1][0], points[1][1], points[2][0], points[2][1], points[3][0], points[3][1]);
  } else {
    points.slice(1).forEach(([x, y]) => ctx.lineTo(x, y));
  }
};

// Marqueur d'extrémité, pointe (ou centre) sur `tip`, orienté selon `angle`.
export const traceMarker = (ctx, marker, [x, y], angle, size) => {
  const at = (forward, side) => [
    x + Math.cos(angle) * forward - Math.sin(angle) * side,
    y + Math.sin(angle) * forward + Math.cos(angle) * side,
  ];
  const polygon = (points) => {
    ctx.beginPath();
    points.forEach(([px, py], i) => (i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)));
    ctx.closePath();
  };
  switch (marker) {
    case 'arrow':
      polygon([at(0, 0), at(-size, size / 2), at(-size, -size / 2)]);
      return true;
    case 'diamond':
      polygon([at(0, 0), at(-size / 2, size / 3), at(-size, 0), at(-size / 2, -size / 3)]);
      return true;
    case 'square':
      polygon([at(size / 3, size / 3), at(size / 3, -size / 3), at(-size / 3, -size / 3), at(-size / 3, size / 3)]);
      return true;
    case 'dot':
      ctx.beginPath();
      ctx.arc(x, y, size / 3, 0, Math.PI * 2, false);
      ctx.closePath();
      return true;
    default:
      return false;
  }
};

// ---------------------------------------------------------------------------
// Poignées de création de lien (éditeur plein écran)

// Une poignée par ancre, posée sur le cadre de la sous-forme (celui du
// Transformer) et poussée de `offset` vers l'extérieur : toujours à la même
// distance de la poignée de redimensionnement voisine, quel que soit le contour
// (arrondi, rond, étoile...) — calée sur le bord réel, elle pouvait venir la
// recouvrir. Le lien, lui, s'accroche bien au bord (cf. getAnchor).
const HANDLE_FRAME_POSITIONS = {
  top: [0.5, 0, 0, -1],
  'top-right': [1, 0, Math.SQRT1_2, -Math.SQRT1_2],
  right: [1, 0.5, 1, 0],
  'bottom-right': [1, 1, Math.SQRT1_2, Math.SQRT1_2],
  bottom: [0.5, 1, 0, 1],
  'bottom-left': [0, 1, -Math.SQRT1_2, Math.SQRT1_2],
  left: [0, 0.5, -1, 0],
  'top-left': [0, 0, -Math.SQRT1_2, -Math.SQRT1_2],
};

export const linkHandlePositions = (sub, offset) => LINK_ANCHORS.map((anchor) => {
  const [fx, fy, dx, dy] = HANDLE_FRAME_POSITIONS[anchor];
  const [x, y] = toComposition(sub, [fx * sub.width + dx * offset, fy * sub.height + dy * offset]);
  return { anchor, x, y };
});
